import { Injectable } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import { tap } from 'rxjs/operators'
import {
  CabinetDetail,
  CabinetDetailResponse,
  CabinetLetterDetailResponse,
  CabinetLetterImagesResponse,
  CabinetLetterListResponse,
  CabinetQuestion,
  CabinetQuestionResponse,
  CabinetUnReadLetterCountResponse,
} from 'src/app/interfaces/cabinet'
import { environment } from 'src/environments/environment'
import { CustomHttpClient } from '../http/http.service'

export const isCabinetLink =
  location.host.includes('askme.cab') ||
  location.host.includes('cabinet.headmost.app') ||
  location.host.includes('cabinet.types.my')

@Injectable({
  providedIn: 'root',
})
export class CabinetService {
  constructor(private http: CustomHttpClient) {}

  private _currentQuestion: CabinetQuestion | undefined
  private _currentQuestionSubject = new Subject<CabinetQuestion>()
  private _cabinet: CabinetDetail | undefined
  private _sampleQuestions: CabinetQuestion[] = []

  get cabinet(): CabinetDetail | undefined {
    return this._cabinet
  }

  set cabinet(data: CabinetDetail | undefined) {
    this._cabinet = data
  }

  get currentQuestion(): CabinetQuestion | undefined {
    return this._currentQuestion
  }

  set currentQuestion(data: CabinetQuestion | undefined) {
    this._currentQuestion = data
    this._currentQuestionSubject.next(data)
  }

  get sampleQuestions(): CabinetQuestion[] {
    return [...this._sampleQuestions]
  }

  getCurrentQuestionSubject(): Observable<CabinetQuestion> {
    return this._currentQuestionSubject.asObservable()
  }

  getCabinetDetail(uniqueName: string): Observable<CabinetDetailResponse> {
    return this.http.get<CabinetDetailResponse>(`${environment.apiUrl}/v1/cabinet/${uniqueName}`).pipe(
      tap((res) => {
        if (res.data) {
          this.cabinet = res.data
        }
      })
    )
  }

  getCabinetLetterImages(): Observable<CabinetLetterImagesResponse> {
    return this.http.get<CabinetLetterImagesResponse>(`${environment.apiUrl}/v1/cabinet/letter-images`)
  }

  postCabinetLetter(uniqueName: string, content: string, imageId: number, hint?: string): Observable<void> {
    return this.http.post(`${environment.apiUrl}/v1/cabinet/letters`, {
      uniqueName,
      content,
      imageId,
      hint,
    })
  }

  createCabinet(): Observable<CabinetDetailResponse> {
    return this.http.post<CabinetDetailResponse>(`${environment.apiUrl}/v1/cabinet`)
  }

  updateCabinet(uniqueName: string, question: string, themeColor: string): Observable<CabinetDetailResponse> {
    const data = { question, themeColor }
    return this.http.patch<CabinetDetailResponse>(`${environment.apiUrl}/v1/cabinet/${uniqueName}`, data)
  }

  getMyLetters(cursor: string | null, pageSize = 10): Observable<CabinetLetterListResponse> {
    const queryParams = { cursor, pageSize }
    return this.http.get<CabinetLetterListResponse>(`${environment.apiUrl}/v1/cabinet/letters/mine`, { queryParams })
  }

  getUnReadLetterCount(): Observable<CabinetUnReadLetterCountResponse> {
    return this.http.get<CabinetUnReadLetterCountResponse>(`${environment.apiUrl}/v1/cabinet/letters/unread/count`)
  }

  getLetterDetail(letterId: number): Observable<CabinetLetterDetailResponse> {
    return this.http.get<CabinetLetterDetailResponse>(`${environment.apiUrl}/v1/cabinet/letters/${letterId}`)
  }

  replyToLetter(letterId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/v1/cabinet/letters/${letterId}/reply`)
  }

  getSampleQuestions(): Observable<CabinetQuestionResponse> {
    return this.http.get<CabinetQuestionResponse>(`${environment.apiUrl}/v1/cabinet/question-templates`).pipe(
      tap((res) => {
        if (res.data && Array.isArray(res.data)) {
          this._sampleQuestions = res.data.sort((a, b) => Number(b.isDefault ?? 0) - Number(a.isDefault ?? 0))
        }
      })
    )
  }
}
